import { useAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Slider } from '@/shared/ui/atoms';

import { useSlideEditorContext } from '../../../context/SlideEditorContext';
import { editorSelectionAtom } from '../../../store/atoms/editorSelectionAtom';
import { ColorSwatch } from '../../atoms/ColorSwatch';

const INPUT_CLS =
  'w-full rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body px-nano py-quark text-ctx-content-base outline-none ts-caption-xxs focus:border-ctx-interactive-primary';

function NumericInput({
  value,
  onChange,
  min,
  max,
  step = 1,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
}) {
  const [local, setLocal] = useState(String(value));
  useEffect(() => setLocal(String(value)), [value]);

  return (
    <input
      type="number"
      value={local}
      min={min}
      max={max}
      step={step}
      onChange={e => setLocal(e.target.value)}
      onBlur={() => {
        const n = parseFloat(local);
        if (!Number.isNaN(n)) onChange(n);
      }}
      className={INPUT_CLS}
    />
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="mb-quark block ts-caption-xxs text-ctx-content-placeholder">{children}</span>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-nano">
      <FieldLabel>{label}</FieldLabel>
      {children}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-mini border-b-hairline border-ctx-layout-border pb-nano last:border-b-none">
      <p className="mb-nano !font-medium uppercase tracking-wide text-ctx-content-placeholder ts-caption-xxs">
        {title}
      </p>
      {children}
    </div>
  );
}

const FONT_FAMILIES = [
  'Inter, sans-serif',
  'Georgia, serif',
  'Arial, sans-serif',
  'Courier New, monospace',
  'Times New Roman, serif',
];

const FONT_WEIGHTS = [
  { label: 'Normal', value: '400' },
  { label: 'Semi', value: '600' },
  { label: 'Bold', value: '700' },
  { label: 'Black', value: '900' },
];

export function PropertiesTab() {
  const { t } = useTranslation('slideEditor');
  const ctx = useSlideEditorContext();
  const [selection] = useAtom(editorSelectionAtom);

  const isText = selection ? ['textbox', 'i-text', 'text'].includes(selection.type) : false;
  const isImage = selection?.type === 'image';
  const isBannerCta = selection?.type === 'group' && selection.href !== undefined;

  return (
    <div className="overflow-y-auto p-nano">
      {/* ── Período de exibição (metadado do slide — sempre visível) ── */}
      <Section title={t('properties.sectionDisplayPeriod')}>
        <div className="grid grid-cols-2 gap-nano">
          <div>
            <FieldLabel>{t('properties.displayPeriodStart')}</FieldLabel>
            <input
              type="date"
              value={ctx.displayPeriod.startDate}
              onChange={e => ctx.setDisplayPeriod(e.target.value, ctx.displayPeriod.endDate)}
              className={INPUT_CLS}
            />
          </div>
          <div>
            <FieldLabel>{t('properties.displayPeriodEnd')}</FieldLabel>
            <input
              type="date"
              value={ctx.displayPeriod.endDate}
              onChange={e => ctx.setDisplayPeriod(ctx.displayPeriod.startDate, e.target.value)}
              className={INPUT_CLS}
            />
          </div>
        </div>
        {!ctx.displayPeriod.startDate && !ctx.displayPeriod.endDate && (
          <p className="mt-quark text-ctx-content-placeholder ts-caption-xxxs">
            {t('properties.displayPeriodAlways')}
          </p>
        )}
      </Section>

      {/* ── Seções de elemento (somente quando há seleção) ── */}
      {!selection && (
        <div className="flex h-[7.5rem] items-center justify-center px-mini">
          <span className="text-center text-ctx-content-placeholder ts-paragraph-xxs">
            {t('properties.noSelection')}
          </span>
        </div>
      )}

      {selection && (
        <>
          {/* Posição & Tamanho — oculto para o banner CTA (posição é fixa) */}
          {!isBannerCta && (
            <Section title={t('properties.sectionTransform')}>
              {/* X/Y/W/H/∠ — grid compacto com rótulos de 1 char */}
              <div className="mb-nano grid grid-cols-[1.5rem_1fr_1.5rem_1fr] items-center gap-x-quark gap-y-quark">
                <span className="ts-caption-xxs text-right text-ctx-content-placeholder">X</span>
                <NumericInput
                  value={selection.x}
                  onChange={v => ctx.setObjectPosition(v, selection.y)}
                />
                <span className="ts-caption-xxs text-right text-ctx-content-placeholder">Y</span>
                <NumericInput
                  value={selection.y}
                  onChange={v => ctx.setObjectPosition(selection.x, v)}
                />
                <span className="ts-caption-xxs text-right text-ctx-content-placeholder">W</span>
                <NumericInput
                  value={selection.width}
                  min={1}
                  onChange={v => ctx.setObjectSize(v, selection.height)}
                />
                <span className="ts-caption-xxs text-right text-ctx-content-placeholder">H</span>
                <NumericInput
                  value={selection.height}
                  min={1}
                  onChange={v => ctx.setObjectSize(selection.width, v)}
                />
                <span className="ts-caption-xxs text-right text-ctx-content-placeholder">∠</span>
                <NumericInput
                  value={selection.angle}
                  min={0}
                  max={360}
                  onChange={ctx.setObjectAngle}
                />
              </div>

              {/* Opacidade — label + % na mesma linha, slider abaixo */}
              <div className="mb-nano">
                <div className="mb-quark flex items-center justify-between">
                  <FieldLabel>{t('properties.opacity')}</FieldLabel>
                  <span className="ts-caption-xxxs text-ctx-content-placeholder">
                    {selection.opacity}%
                  </span>
                </div>
                <Slider
                  min={0}
                  max={100}
                  value={selection.opacity}
                  onChange={e => ctx.setObjectOpacity(Number(e.target.value))}
                  aria-label={t('properties.opacity')}
                  className="w-full"
                />
              </div>
            </Section>
          )}

          {/* Aparência */}
          {!isImage && (
            <Section title={t('properties.sectionAppearance')}>
              <Field label={t('properties.fill')}>
                <ColorSwatch
                  value={selection.fill}
                  onChange={ctx.setFill}
                  label={t('properties.fill')}
                />
              </Field>

              <Field label={t('properties.stroke')}>
                <div className="flex items-center gap-nano">
                  <div className="min-w-none flex-1">
                    <ColorSwatch
                      value={selection.stroke}
                      onChange={c => ctx.setStroke(c, selection.strokeWidth)}
                      label={t('properties.stroke')}
                    />
                  </div>
                  <div className="flex w-[3.5rem] shrink-0 items-center gap-quark">
                    <NumericInput
                      value={selection.strokeWidth}
                      min={0}
                      onChange={w => ctx.setStroke(selection.stroke, w)}
                    />
                    <span className="shrink-0 ts-caption-xxxs text-ctx-content-placeholder">
                      px
                    </span>
                  </div>
                </div>
              </Field>
            </Section>
          )}

          {/* Link — Banner CTA */}
          {isBannerCta && (
            <Section title={t('properties.sectionLink')}>
              <Field label={t('properties.link')}>
                <input
                  type="url"
                  value={selection.href ?? ''}
                  onChange={e => ctx.setBannerCtaHref(e.target.value)}
                  placeholder="https://..."
                  className={INPUT_CLS}
                />
              </Field>
            </Section>
          )}

          {/* Tipografia */}
          {isText && (
            <Section title={t('properties.sectionTypography')}>
              <div className="mb-nano">
                <textarea
                  value={selection.text}
                  onChange={e => ctx.setTextProps({ text: e.target.value })}
                  className="min-h-md w-full resize-y rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-body px-nano py-quark text-ctx-content-base outline-none ts-caption-xxs focus:border-ctx-interactive-primary"
                />
              </div>

              <Field label={t('properties.fontFamily')}>
                <select
                  value={selection.fontFamily}
                  onChange={e => ctx.setTextProps({ fontFamily: e.target.value })}
                  className={INPUT_CLS}
                >
                  {FONT_FAMILIES.map(f => (
                    <option key={f} value={f}>
                      {f.split(',')[0]}
                    </option>
                  ))}
                </select>
              </Field>

              <div className="grid grid-cols-2 gap-nano">
                <Field label={t('properties.fontSize')}>
                  <NumericInput
                    value={selection.fontSize}
                    min={6}
                    max={300}
                    onChange={v => ctx.setTextProps({ fontSize: v })}
                  />
                </Field>

                <Field label={t('properties.fontWeight')}>
                  <select
                    value={selection.fontWeight}
                    onChange={e => ctx.setTextProps({ fontWeight: e.target.value })}
                    className={INPUT_CLS}
                  >
                    {FONT_WEIGHTS.map(w => (
                      <option key={w.value} value={w.value}>
                        {w.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label={t('properties.fontStyle')}>
                  <select
                    value={selection.fontStyle}
                    onChange={e => ctx.setTextProps({ fontStyle: e.target.value })}
                    className={INPUT_CLS}
                  >
                    <option value="normal">Normal</option>
                    <option value="italic">Itálico</option>
                  </select>
                </Field>

                <Field label={t('properties.lineHeight')}>
                  <NumericInput
                    value={selection.lineHeight}
                    min={0.5}
                    max={5}
                    step={0.1}
                    onChange={v => ctx.setTextProps({ lineHeight: v })}
                  />
                </Field>

                <Field label={t('properties.charSpacing')}>
                  <NumericInput
                    value={selection.charSpacing}
                    min={-500}
                    max={1000}
                    onChange={v => ctx.setTextProps({ charSpacing: v })}
                  />
                </Field>
              </div>
            </Section>
          )}
        </>
      )}
    </div>
  );
}
